const DIALOG_ID = 'dwp-navigator__iframe-dialog';
const HANDLE_SELECTOR = '.tc-dwp-navigator-resize-handle';
const INIT_FLAG = 'data-tc-dwp-navigator-resize';

const MIN_WIDTH_PX = 500;
const MIN_HEIGHT_PX = 400;
const MOBILE_BREAKPOINT_PX = 768;
const MAX_WIDTH_CAP_PX = 960;
const MAX_HEIGHT_CAP_PX = 900;
const VIEWPORT_MARGIN_PX = 16;
const DIALOG_POLL_MS = 100;
const DIALOG_POLL_TIMEOUT_MS = 30000;

const getMaxWidthPx = () => Math.min(window.innerWidth * 0.9, MAX_WIDTH_CAP_PX);

const getMaxHeightPx = (anchorBottomPx) => {
  const cap = Math.min(window.innerHeight * 0.9, MAX_HEIGHT_CAP_PX);
  const available = anchorBottomPx - VIEWPORT_MARGIN_PX;
  return Math.min(cap, Math.max(available, MIN_HEIGHT_PX));
};

const isDesktopViewport = () => window.innerWidth >= MOBILE_BREAKPOINT_PX;

const clampWidth = (widthPx) => {
  const max = getMaxWidthPx();
  return Math.min(Math.max(widthPx, MIN_WIDTH_PX), max);
};

const clampHeight = (heightPx, anchorBottomPx) => {
  const max = getMaxHeightPx(anchorBottomPx);
  return Math.min(Math.max(heightPx, MIN_HEIGHT_PX), max);
};

const applyDialogHeight = (dialog, heightPx) => {
  dialog.style.height = `${heightPx}px`;
  dialog.style.setProperty('--dwp-navigator-iframe-height', `${heightPx}px`);
};

const pinDialogBottom = (dialog, anchorBottomPx) => {
  dialog.style.top = 'auto';
  dialog.style.bottom = `${window.innerHeight - anchorBottomPx}px`;
};

const clearDialogSizeOverrides = (dialog) => {
  dialog.style.width = '';
  dialog.style.height = '';
  dialog.style.removeProperty('--dwp-navigator-iframe-height');
};

export const initDwpNavigatorPanelResize = () => {
  let disposed = false;
  let dialog = null;
  let pollTimer = null;
  let pollTimeout = null;
  let activeResize = null;
  let captureHandle = null;

  const onPointerMove = (event) => {
    if (!activeResize || !dialog) return;

    if (activeResize.mode === 'width' || activeResize.mode === 'both') {
      const deltaX = event.clientX - activeResize.startX;
      const nextWidth = clampWidth(activeResize.startWidth - deltaX);
      dialog.style.width = `${nextWidth}px`;
    }

    if (activeResize.mode === 'height' || activeResize.mode === 'both') {
      const nextHeight = clampHeight(
        activeResize.startHeight + (activeResize.startY - event.clientY),
        activeResize.anchorBottom,
      );
      applyDialogHeight(dialog, nextHeight);
      pinDialogBottom(dialog, activeResize.anchorBottom);
    }
  };

  const endResize = (event) => {
    if (!activeResize) return;
    if (event?.pointerId != null && captureHandle?.hasPointerCapture?.(event.pointerId)) {
      captureHandle.releasePointerCapture(event.pointerId);
    }
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endResize);
    window.removeEventListener('pointercancel', endResize);
    activeResize = null;
    captureHandle = null;
  };

  const onHandlePointerDown = (mode) => (event) => {
    if (!dialog || !isDesktopViewport() || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const rect = dialog.getBoundingClientRect();
    activeResize = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
      anchorBottom: rect.bottom,
    };

    if (mode === 'height' || mode === 'both') {
      pinDialogBottom(dialog, activeResize.anchorBottom);
    }

    captureHandle = event.currentTarget;
    captureHandle.setPointerCapture(event.pointerId);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endResize);
    window.addEventListener('pointercancel', endResize);
  };

  const createHandle = (className, ariaLabel, mode) => {
    const handle = document.createElement('div');
    handle.className = `tc-dwp-navigator-resize-handle ${className}`;
    handle.setAttribute('role', 'separator');
    handle.setAttribute('aria-label', ariaLabel);
    handle.addEventListener('pointerdown', onHandlePointerDown(mode));
    return handle;
  };

  const removeHandleElements = () => {
    endResize();
    if (dialog) {
      dialog.querySelectorAll(HANDLE_SELECTOR).forEach((node) => node.remove());
    }
  };

  const detachFromDialog = () => {
    removeHandleElements();
    if (dialog) {
      dialog.removeAttribute(INIT_FLAG);
      dialog = null;
    }
  };

  const syncHandleForViewport = () => {
    if (disposed || !dialog) return;
    if (!isDesktopViewport()) {
      removeHandleElements();
      return;
    }
    if (dialog.querySelector(HANDLE_SELECTOR)) return;

    dialog.appendChild(createHandle(
      'tc-dwp-navigator-resize-handle--left',
      'Resize chat panel width',
      'width',
    ));
    dialog.appendChild(createHandle(
      'tc-dwp-navigator-resize-handle--top',
      'Resize chat panel height',
      'height',
    ));
    dialog.appendChild(createHandle(
      'tc-dwp-navigator-resize-handle--corner',
      'Resize chat panel width and height',
      'both',
    ));
  };

  const attachToDialog = (node) => {
    if (disposed || !node) return false;

    if (node.getAttribute(INIT_FLAG) === 'true') {
      dialog = node;
      syncHandleForViewport();
      return true;
    }

    dialog = node;
    dialog.setAttribute(INIT_FLAG, 'true');
    syncHandleForViewport();
    return true;
  };

  const onWindowResize = () => {
    if (disposed) return;

    if (!dialog || !document.body.contains(dialog)) {
      detachFromDialog();
      tryFindDialog();
      return;
    }

    if (!isDesktopViewport()) {
      removeHandleElements();
      clearDialogSizeOverrides(dialog);
      return;
    }

    syncHandleForViewport();

    if (dialog.style.width) {
      const parsedWidth = parseFloat(dialog.style.width);
      if (!Number.isNaN(parsedWidth)) {
        dialog.style.width = `${clampWidth(parsedWidth)}px`;
      }
    }

    if (dialog.style.height) {
      const parsedHeight = parseFloat(dialog.style.height);
      const anchorBottom = dialog.getBoundingClientRect().bottom;
      if (!Number.isNaN(parsedHeight)) {
        applyDialogHeight(dialog, clampHeight(parsedHeight, anchorBottom));
        pinDialogBottom(dialog, anchorBottom);
      }
    }
  };

  const tryFindDialog = () => {
    const node = document.getElementById(DIALOG_ID);
    if (!node) return false;
    return attachToDialog(node);
  };

  window.addEventListener('resize', onWindowResize);

  if (!tryFindDialog()) {
    pollTimer = window.setInterval(() => {
      if (tryFindDialog() && pollTimer) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    }, DIALOG_POLL_MS);

    pollTimeout = window.setTimeout(() => {
      if (pollTimer) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    }, DIALOG_POLL_TIMEOUT_MS);
  }

  return () => {
    disposed = true;
    if (pollTimer) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
    if (pollTimeout) {
      window.clearTimeout(pollTimeout);
      pollTimeout = null;
    }
    window.removeEventListener('resize', onWindowResize);
    detachFromDialog();
  };
};
